import { STRATEGY_CATEGORIES, STRATEGY_TOOLS, type StrategyCategory } from '@ads/shared';
import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import { BarList } from '../components/BarList';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { SmallDial } from '../components/SmallDial';
import { Tabs } from '../components/Tabs';
import { rich } from '../lib/format';
import { useProject } from '../lib/queries';

const TABS = [['todas', 'Todas'], ...STRATEGY_CATEGORIES] as ReadonlyArray<readonly [StrategyCategory | 'todas', string]>;

export function Strategy() {
  useViewMeta({ nav: 'strategy', crumb: ['Strategy', <>Etapa <span className="n">02</span> de 11</>], j: .18 });
  const project = useProject();
  const nav = useNavigate();
  const [tab, setTab] = useState<StrategyCategory | 'todas'>('todas');
  const p = project.data;
  const summary = new Map((p?.tools ?? []).map(t => [t.key, t]));
  const visible = STRATEGY_TOOLS.filter(t => tab === 'todas' || t.category === tab);
  const inProgress = p?.tools.filter(t => t.state === 'now').length ?? 0;
  const open = (key: string) => nav(`/strategy/${key}`);

  return (
    <section className="view on" data-v="strategy">
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Strategy</span><b>02 / 11</b></div>
          <h1>Organize a estratégia<br />da sua organização.</h1>
          <p className="lede">Escolha uma ferramenta, acompanhe a evolução e avance etapa por etapa com apoio da IA.</p>
        </div>
        <div className="tools"><Tabs options={TABS} value={tab} onChange={setTab} label="Categorias" /></div>
      </div>

      <div className="grid2 strategy-grid">
        <div>
          <div className="blocks-head"><h2>As 10 ferramentas</h2><span className="meta">{inProgress} em andamento</span></div>
          {visible.map(t => {
            const s = summary.get(t.key);
            const state = s?.state ?? 'todo';
            const Icon = Ic.TOOL_ICON[t.key];
            const next = p?.nextSteps[0]?.tool === t.key && state === 'todo';
            return (
              <button key={t.key} className="tool" type="button" data-state={state} style={{ '--p': s?.progress ?? 0 } as CSSProperties} onClick={() => open(t.key)}>
                <span className="no">{t.no}</span><span className="ic"><Icon /></span>
                <span className="tx"><b>{t.name}</b><span>{t.desc}</span></span>
                <span className="st">
                  {state === 'now' && <span className="tag-st now">Em andamento</span>}
                  {state === 'done' && <span className="tag-st done">Concluída</span>}
                  {next && <span className="tag-st">Próxima</span>}
                  <span>{state === 'todo' ? '—' : `${Math.round((s?.progress ?? 0) * 100)}%`}</span>
                  <Ic.ChevronRight className="arrow" />
                </span>
              </button>
            );
          })}
        </div>

        <div className="stack">
          <div className="card">
            <h3>Score estratégico</h3>
            <p className="sub">Consistência estratégica atual</p>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 14px' }}>
              <SmallDial value={p?.strategic.score ?? 0} seg={5} size={124} label={p?.strategic.score ?? '—'} caption="Score" />
            </div>
            <BarList items={(p?.strategic.axes ?? []).map(a => ({ label: a.label, value: a.value }))} />
          </div>

          <div className="card">
            <h3>Próximos passos</h3>
            <ol className="olist">
              {(p?.nextSteps ?? []).map((s, i) => (
                <li key={s.tool} className="clickable" onClick={() => open(s.tool)}>
                  <span className="k">{String(i + 1).padStart(2, '0')}</span>
                  <span className="v go">{s.label}<Ic.ChevronRight /></span>
                </li>
              ))}
              {p && !p.nextSteps.length && <li><span className="k">—</span><span className="v">Todas as ferramentas concluídas. Consolide o DNA.</span></li>}
            </ol>
          </div>

          <div className="card card-brand">
            <h3><Ic.Sparkle />Leitura da IA</h3>
            <p className="reading">{p ? rich(p.strategic.reading) : ' '}</p>
          </div>
        </div>
      </div>

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => window.print()}>Exportar diagnóstico</button>
        <button className="btn-primary" type="button" onClick={() => open(p?.nextSteps[0]?.tool ?? 'gaps')}>
          Continuar diagnóstico<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}
